import Decimal from 'decimal.js';

// Pure straight-line depreciation math, shared by the API (posting depreciation)
// and the web (previewing an asset's schedule before it's saved). This raw row has
// no `posted` flag — the API adds that when it builds the saved asset's schedule.
export interface DepreciationScheduleRow {
  periodDate: string; // month-end ISO the depreciation is dated to
  amount: string; // 4dp
  accumulated: string; // 4dp running total
  bookValue: string; // 4dp cost − accumulated
}

/**
 * When depreciation starts within the in-service month.
 * - `full_month`: the whole in-service month counts (equipment, vehicles, furniture).
 * - `mid_month`: the IRS convention for real property (27.5-year residential rental,
 *   39-year nonresidential): the first and last months each count as half a month,
 *   so a 330-month life spans 331 calendar months.
 */
export type DepreciationConvention = 'full_month' | 'mid_month';

function ym(iso: string): [number, number] {
  const [y, m] = iso.split('-').map(Number) as [number, number];
  return [y, m];
}

export function monthEndIso(y: number, mo: number): string {
  const lastDay = new Date(Date.UTC(y, mo, 0)).getUTCDate(); // day 0 of next month = last day of `mo`
  return `${y}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

function addMonths(y: number, mo: number, n: number): [number, number] {
  const total = y * 12 + (mo - 1) + n;
  return [Math.floor(total / 12), (total % 12) + 1];
}

/** The last day of the month before the one `isoDate` falls in — "the most recent closed month". */
export function previousMonthEnd(isoDate: string): string {
  const [y, m] = ym(isoDate);
  const [py, pm] = addMonths(y, m, -1);
  return monthEndIso(py, pm);
}

/**
 * Straight-line depreciation schedule: equal monthly amounts (rounded to cents),
 * with the final period absorbing any rounding so the total equals cost − salvage
 * exactly. Depreciation starts in the in-service month; under the mid-month
 * convention that month and the one after the nominal life each carry half a
 * month, so the schedule is one period longer than the life.
 */
export function straightLineSchedule(
  cost: string,
  salvage: string,
  usefulLifeMonths: number,
  inServiceDate: string,
  convention: DepreciationConvention = 'full_month',
): DepreciationScheduleRow[] {
  const depreciable = new Decimal(cost).minus(salvage);
  const periods: DepreciationScheduleRow[] = [];
  if (depreciable.lessThanOrEqualTo(0) || usefulLifeMonths < 1) return periods;

  const perMonth = depreciable.div(usefulLifeMonths).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const half = perMonth.div(2).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const count = convention === 'mid_month' ? usefulLifeMonths + 1 : usefulLifeMonths;
  const [sy, sm] = ym(inServiceDate);
  let accumulated = new Decimal('0');

  for (let i = 0; i < count; i++) {
    const [y, mo] = addMonths(sy, sm, i);
    let amount: Decimal;
    if (i === count - 1) amount = depreciable.minus(accumulated);
    else if (convention === 'mid_month' && i === 0) amount = half;
    else amount = perMonth;
    accumulated = accumulated.plus(amount);
    periods.push({
      periodDate: monthEndIso(y, mo),
      amount: amount.toFixed(4),
      accumulated: accumulated.toFixed(4),
      bookValue: new Decimal(cost).minus(accumulated).toFixed(4),
    });
  }
  return periods;
}

/**
 * The schedule periods that should be posted now: dated on/before `throughDate`
 * and after `lastDepreciatedThrough` (or from the start if never depreciated).
 */
export function duePeriods(
  schedule: DepreciationScheduleRow[],
  lastDepreciatedThrough: string | null,
  throughDate: string,
): DepreciationScheduleRow[] {
  return schedule.filter(
    (p) => p.periodDate <= throughDate && (!lastDepreciatedThrough || p.periodDate > lastDepreciatedThrough),
  );
}
